import { useState } from "react";
import { MapPin, Pencil } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { Insured } from "@shared/schema";
import { EditAddressDialog } from "./EditAddressDialog";

interface AddressBlock {
  label: string;
  kind: "physical" | "mailing";
  lines: string[] | null;
}

interface Props {
  insured: Insured;
}

function formatInsuredAddress(
  street: string | null | undefined,
  city: string | null | undefined,
  state: string | null | undefined,
  zip: string | null | undefined,
  country: string | null | undefined,
): string[] | null {
  if (!street) return null;
  const cityStateZip = [city, state, zip].filter(Boolean).join(", ");
  const lines = [street];
  if (cityStateZip) lines.push(cityStateZip);
  if (country && country !== "US") lines.push(country);
  return lines;
}

export function AddressesSection({ insured }: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [kind, setKind] = useState<"physical" | "mailing">("physical");

  const blocks: AddressBlock[] = [
    {
      label: "Policy / Garaging",
      kind: "physical",
      lines: formatInsuredAddress(
        insured.phyStreet,
        insured.phyCity,
        insured.phyState,
        insured.phyZip,
        insured.phyCountry,
      ),
    },
    {
      label: "Mailing",
      kind: "mailing",
      lines: formatInsuredAddress(
        insured.mailingStreet,
        insured.mailingCity,
        insured.mailingState,
        insured.mailingZip,
        insured.mailingCountry,
      ),
    },
  ];

  function openEdit(blockKind: "physical" | "mailing") {
    setKind(blockKind);
    setDialogOpen(true);
  }

  return (
    <Card className="overflow-hidden" data-testid="addresses-section">
      <div className="flex items-center gap-2 px-6 py-4 border-b border-border">
        <MapPin className="w-5 h-5 text-foreground" aria-hidden="true" />
        <h3 className="text-base font-semibold">Addresses</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
        {blocks.map((block) => (
          <div
            key={block.label}
            className="rounded-lg border border-border p-4 relative"
            data-testid={`address-${block.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-muted-foreground">{block.label}</p>
              <Button
                size="sm"
                variant="ghost"
                className="cursor-pointer h-7 px-2"
                onClick={() => openEdit(block.kind)}
                aria-label={`Edit ${block.label} address`}
                data-testid={`address-${block.kind}-edit`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </Button>
            </div>
            {block.lines ? (
              <div className="space-y-0.5 text-sm">
                {block.lines.map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">--</p>
            )}
          </div>
        ))}
      </div>

      <EditAddressDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        insured={insured}
        kind={kind}
      />
    </Card>
  );
}
